import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { PaperProps, TypographyProps } from '@mui/material';
import type * as React from 'react';

import { FontAwesomeScalableIcon, styled } from '@mend/mui';
import { Box, Paper, Typography } from '@mui/material';

const StyledPaper = styled((props: PaperProps) => <Paper elevation={2} {...props} />)(
  ({ theme }) => ({
    maxWidth: '300px',
    width: '100%',
    padding: theme.spacing(4),
    borderRadius: Number(theme.shape.borderRadius) * 2.5,
    textAlign: 'center',
  })
);

const StyledIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
}));

interface BaseRootProps {
  title: string | React.ReactElement;
  description?: string | React.ReactElement;
  children?: React.ReactNode;
}

interface RootPropsWithFaIcon extends BaseRootProps {
  faIcon: IconDefinition;
  icon?: never;
}

interface RootPropsWithIcon extends BaseRootProps {
  icon: React.ReactElement;
  faIcon?: never;
}

export function Root(props: RootPropsWithFaIcon | RootPropsWithIcon) {
  return (
    <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
      <StyledPaper>
        {props.faIcon ? (
          <StyledIconWrapper color="text.primary" fontSize={58}>
            <FontAwesomeScalableIcon color="inherit" fontSize="inherit" icon={props.faIcon} />
          </StyledIconWrapper>
        ) : (
          <StyledIconWrapper>{props.icon}</StyledIconWrapper>
        )}

        {typeof props.title === 'string' ? <Title>{props.title}</Title> : props.title}

        {typeof props.description === 'string' ? (
          <Description>{props.description}</Description>
        ) : (
          props.description
        )}

        {props.children}
      </StyledPaper>
    </Box>
  );
}

function Title(props: TypographyProps) {
  return <Typography component="p" variant="subtitle2" textTransform="capitalize" {...props} />;
}

export function Description(props: TypographyProps) {
  return <Typography component="p" variant="body2" color="text.secondary" mt={1} {...props} />;
}
